import { 
    Button, 
    DatePicker, 
    Form, 
    Input, 
    Radio, 
    Card, 
    Avatar, 
    Row, 
    Col, 
    Typography,
    Space,
    Divider,
    Tag,
    message,
    Upload
} from 'antd'
import "./profile.styles.scss"
import { useDispatchRoot, useSelectorRoot } from '../../redux/store'
import { useEffect, useState } from 'react'
import {
    editProfileRequest, 
    getProfileRequest,
    getUserInfoRequest
} from "../../redux/controller";
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import moment from 'moment'
import { Rule } from 'antd/lib/form';
import { motion } from "framer-motion";
import { IStaffChange } from '../../common/staff.interface';
import { useNavigate } from 'react-router-dom';
import { 
    UserOutlined, 
    MailOutlined, 
    PhoneOutlined, 
    HomeOutlined,
    CalendarOutlined,
    TeamOutlined,
    EditOutlined,
    SaveOutlined,
    CameraOutlined,
    StarOutlined,
    GiftOutlined
} from '@ant-design/icons';

dayjs.extend(customParseFormat);

const { Title, Text } = Typography;
const regexPhoneNumber = /^0(1\d{9}|3\d{8}|5\d{8}|7\d{8}|8\d{8}|9\d{8})$/;

const Profile = () => {
    const [form] = Form.useForm();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatchRoot();
    const navigate = useNavigate();
    
    const { profile } = useSelectorRoot((state) => state.management);
    const { userName, userMail, tokenLogin } = useSelectorRoot((state) => state.login);

    useEffect(() => {
        dispatch(getProfileRequest());
        if (tokenLogin) {
            dispatch(getUserInfoRequest(tokenLogin));
        }
    }, [dispatch, tokenLogin]);

    useEffect(() => {
        if (profile) {
            const profileData = {
                fullName: profile?.fullName || userName,
                email: profile?.email || userMail,
                gender: profile?.gender,
                phoneNumber: profile?.phoneNumber,
                dob: profile?.dob ? moment(profile?.dob) : null,
                address: profile?.address,
            }
            form.setFieldsValue(profileData);
        }
    }, [profile, form, userName, userMail]);

    const phoneValidator = (
        rule: Rule,
        value: string,
        callback: (message?: string) => void
    ) => {
        if (!value) {
            callback();
        } else if (!regexPhoneNumber.test(value)) {
            callback("Số điện thoại không hợp lệ");
        } else {
            callback();
        }
    };

    const onFinish = async (values: any) => {
        setLoading(true);
        try {
            const bodyRequest: IStaffChange = {
                fullName: values.fullName,
                phoneNumber: values.phoneNumber,
                address: values.address,
                dob: values.dob ? new Date(values.dob) : undefined,
                gender: values.gender,
            }
            dispatch(editProfileRequest(bodyRequest));
            setIsEditing(false);
            message.success('Cập nhật thông tin thành công!');
        } catch (error) {
            message.error('Cập nhật thông tin thất bại!');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setIsEditing(false);
    };

    const getVipLevel = () => {
        // Mock VIP data - should come from profile in real app
        return {
            level: "VIP Silver",
            points: 1500,
            color: "#C0C0C0"
        };
    };

    const vipInfo = getVipLevel();

    return (
        <div className='profile-page'>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="profile-container"
            >
                {/* Header Card */}
                <Card className="profile-header-card">
                    <Row gutter={24} align="middle">
                        <Col xs={24} sm={8} md={6}>
                            <div className="avatar-section">
                                <Avatar 
                                    size={120} 
                                    icon={<UserOutlined />}
                                    className="profile-avatar"
                                />
                                <Button 
                                    className="avatar-upload-btn"
                                    icon={<CameraOutlined />}
                                    shape="circle"
                                    size="small"
                                />
                            </div>
                        </Col>
                        <Col xs={24} sm={16} md={18}>
                            <div className="profile-header-info">
                                <Title level={2} className="profile-name">
                                    {profile?.fullName || userName || 'Chưa cập nhật'}
                                </Title>
                                <Text className="profile-email">
                                    <MailOutlined /> {profile?.email || userMail || 'Chưa cập nhật'}
                                </Text>
                                <div className="profile-tags">
                                    <Tag 
                                        color={vipInfo.color} 
                                        icon={<StarOutlined />}
                                        className="vip-tag"
                                    >
                                        {vipInfo.level}
                                    </Tag>
                                    <Tag 
                                        color="gold" 
                                        icon={<GiftOutlined />}
                                        className="points-tag"
                                    >
                                        {vipInfo.points} điểm
                                    </Tag>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Profile Form Card */}
                <Card 
                    className="profile-form-card"
                    title={
                        <div className="card-title">
                            <UserOutlined /> Thông tin cá nhân
                        </div>
                    }
                    extra={
                        !isEditing ? (
                            <Button 
                                type="primary" 
                                icon={<EditOutlined />}
                                onClick={() => setIsEditing(true)}
                                className="edit-button"
                            >
                                Chỉnh sửa
                            </Button>
                        ) : null
                    }
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        className="profile-form"
                    >
                        <Row gutter={24}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={
                                        <Space>
                                            <UserOutlined />
                                            <span>Họ và tên</span>
                                        </Space>
                                    }
                                    name="fullName"
                                    rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
                                >
                                    <Input 
                                        placeholder="Nhập họ và tên"
                                        disabled={!isEditing}
                                        className="profile-input"
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={
                                        <Space>
                                            <MailOutlined />
                                            <span>Email</span>
                                        </Space>
                                    }
                                    name="email"
                                >
                                    <Input 
                                        disabled={true}
                                        className="profile-input disabled"
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={
                                        <Space>
                                            <PhoneOutlined />
                                            <span>Số điện thoại</span>
                                        </Space>
                                    }
                                    name="phoneNumber"
                                    rules={[
                                        {
                                            validator: phoneValidator,
                                        },
                                    ]}
                                >
                                    <Input 
                                        placeholder="Nhập số điện thoại"
                                        disabled={!isEditing}
                                        className="profile-input"
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={
                                        <Space>
                                            <CalendarOutlined />
                                            <span>Ngày sinh</span>
                                        </Space>
                                    }
                                    name="dob"
                                >
                                    <DatePicker 
                                        placeholder="Chọn ngày sinh"
                                        disabled={!isEditing}
                                        className="profile-input"
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24}>
                                <Form.Item
                                    label={
                                        <Space>
                                            <HomeOutlined />
                                            <span>Địa chỉ</span>
                                        </Space>
                                    }
                                    name="address"
                                >
                                    <Input.TextArea 
                                        placeholder="Nhập địa chỉ"
                                        disabled={!isEditing}
                                        className="profile-textarea"
                                        rows={3}
                                        maxLength={255}
                                        showCount
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={
                                        <Space>
                                            <TeamOutlined />
                                            <span>Giới tính</span>
                                        </Space>
                                    }
                                    name="gender"
                                >
                                    <Radio.Group 
                                        disabled={!isEditing}
                                        className="gender-radio-group"
                                    >
                                        <Radio value={true}>Nam</Radio>
                                        <Radio value={false}>Nữ</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                        </Row>

                        {isEditing && (
                            <>
                                <Divider />
                                <div className="form-actions">
                                    <Space>
                                        <Button 
                                            onClick={handleCancel}
                                            className="cancel-button"
                                        >
                                            Hủy
                                        </Button>
                                        <Button 
                                            type="primary" 
                                            htmlType="submit"
                                            loading={loading}
                                            icon={<SaveOutlined />}
                                            className="save-button"
                                        >
                                            Lưu thay đổi
                                        </Button>
                                    </Space>
                                </div>
                            </>
                        )}
                    </Form>
                </Card>

                {/* Statistics Card */}
                <Card 
                    className="profile-stats-card"
                    title={
                        <div className="card-title">
                            <StarOutlined /> Thống kê hoạt động
                        </div>
                    }
                >
                    <Row gutter={24}>
                        <Col xs={12} sm={6}>
                            <div className="stat-item">
                                <div className="stat-number">15</div>
                                <div className="stat-label">Đơn hàng</div>
                            </div>
                        </Col>
                        <Col xs={12} sm={6}>
                            <div className="stat-item">
                                <div className="stat-number">25</div>
                                <div className="stat-label">Sách đã mua</div>
                            </div>
                        </Col>
                        <Col xs={12} sm={6}>
                            <div className="stat-item">
                                <div className="stat-number">{vipInfo.points}</div>
                                <div className="stat-label">Điểm tích lũy</div>
                            </div>
                        </Col>
                        <Col xs={12} sm={6}>
                            <div className="stat-item">
                                <div className="stat-number">120h</div>
                                <div className="stat-label">Thời gian đọc</div>
                            </div>
                        </Col>
                    </Row>
                </Card>
            </motion.div>
        </div>
    )
}

export default Profile